import React, { useState } from "react";
import {
  Container,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Grid,
  Button,
  Modal,
  Dialog,
  IconButton,
  DialogContent,
  Stack,
  Link,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import "./AboutView.scss";
import { LazyLoadImage } from "react-lazy-load-image-component";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import onlyBlessYSiso from "../../assets/onlyBlessysiso.png";
import TitleBlockCard from "../../components/card/TitleBlock/TitleBlock";
import { LOCALIZATION } from "../../utils/localization";
import SocialMediaAdd from "../../components/SocialMediaAdd/SocialMediaAdd";
import valmikiImg from "../../assets/temple-wide-carousel.svg";
import peopleImg from "../../assets/people-img.jpeg";
import howBeganBanner from "../../assets/How-began-banner.jpeg";
import isoIcon from "../../assets/certificate/iso-27001.jpeg";
import socIcon from "../../assets/certificate/soc-2-Type-2.jpeg";
import dssIcon from "../../assets/certificate/pci-dss.jpeg";
import ImageView from "../../components/ImageView/ImageView";
import {
  AvinashPandey,
  Debaditya,
  SaheliDas,
  SarbajitDutta,
  SouravSaha,
  SubhamSen,
  UdayShankar,
} from "../../utils/imageConstants";

const AboutView = ({ parentViewCheck }: any) => {
  const navigate = useNavigate();
  const [openBioId, setOpenBioId] = useState(null);
  const { t } = useTranslation();
  const handleReadBioOpen = (id: any) => {
    setOpenBioId(id);
  };

  const handleReadBioClose = () => {
    setOpenBioId(null);
  };

  return (
    <Box
      className={`${
        parentViewCheck ? "pageContainer" : "pageContainerAboutViewNative"
      } aboutView`}
    >
      <Container className="container padding-0">
        <div className="sectionHead text-center style-4 mb-40">
          <small className="title_small d-none"></small>
          <Typography className="mainTitle mb-30">
            About <span>Us</span>
          </Typography>
        </div>
        <div className="aboutViewInfo mb-40">
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
            mb={5}
            className="atGlanceSec cardBg"
          >
            <Box className="glanceText">
              <Typography variant="h5" gutterBottom mb={2} className="fw-bold">
                At a Glance
              </Typography>
              <Typography paragraph>
                Born out of a desire to make spirituality more accessible,
                Temple Dekho is a platform that blends tradition with
                technology, bringing the most popular and sacred temples of
                India right to your fingertips. A faith-tech product by Omkara
                Infotech Pvt Ltd (CIN: U72900WB2022PTC258090), based in Kolkata,
                Temple Dekho was founded in early 2024 to revolutionize how
                devotees connect with their faith in the digital era. Whether
                you are in India or abroad, our goal is to make pujas,
                chadhavas, and darshans just a click away.
              </Typography>
              <Typography paragraph>
                Today, Temple Dekho has a base of more than 1 Lakh devotees
                across India, the US, the UK, and Canada. We have onboareded
                over 50+ most sacred temples of India and coordinated with 100+
                experienced temple partners to ensure that every offering
                reaches the deity with complete devotion and precision. Since
                our inception, we have dispatched over 10,000 prasad boxes with
                puja samagri pan India, each packed with care and sanctity.
                Temple Dekho has impacted lives of thousands of devotees who
                were seeking divine intervention and has found spiritual solace
                through our services.
              </Typography>
              <Typography paragraph>
                We take data security and user trust very seriously. With PCI
                DSS, ISO 27001, and SOC 2 TYPE 2 compliance, Temple Dekho
                ensures that your personal information and spiritual journey
                remain protected and verified.
              </Typography>
            </Box>
            <Box className="glanceImg">
              <img src={valmikiImg} alt="Image"></img>
            </Box>
          </Stack>

          <Box mb={5} className="howItBegan">
            {/* Overlapping image box */}
            <Box className="howItBeganImage">
              <img src={howBeganBanner} alt="Banner" />
            </Box>

            {/* Main content card */}
            <Box className="howItBeganCard">
              <Typography variant="h5" className="fw-bold howItBeganTitle">
                How It Began
              </Typography>

              <Typography paragraph>
                One of our teammates knew a guy living in Bengaluru who wanted
                to offer a puja at Baba Baidyanath Dham in Jharkhand for his
                mother battling illness in Kolkata. She couldn’t travel because
                of her health, and neither could he because of his job and
                responsibilities. He searched for ways to make their wish come
                true—only to be met with disappointments...
              </Typography>
              <Typography paragraph>This is not an isolated story.</Typography>
              <Typography paragraph>
                Often there are moments in life when nothing seems to work—when
                challenges arise that no logic and no amount of money can solve.
                Doctors do their part, science steps in… And yet, deep within,
                the soul still yearns for a miracle, a divine intervention.
              </Typography>
              <Typography paragraph>
                We have all been there, waiting outside an ICU, whispering a
                silent prayer. In those raw, helpless moments, we make promises
                not out loud, but somewhere deep inside:
              </Typography>
              <Typography paragraph>
                “If my mother comes back home healthy, I’ll offer a puja at
                Shirdi”. “If my child recovers, I’ll visit Kamakhya.”
              </Typography>
              <Typography paragraph>
                These vows, made in desperation, hold our deepest devotion that
                often gets buried under the weight of daily life, distance, and
                circumstances.
              </Typography>
              <Typography paragraph>
                That’s where Temple Dekho took birth—not as an idea, but as an
                answer.
              </Typography>
              <Typography paragraph>
                A team of believers, doers, and seekers came together with a
                single mission: Can we make it easier for people to stay
                connected with their spiritual roots, no matter where or in what
                condition they are?
              </Typography>
              <Typography paragraph>
                For us, it is not about creating something new. It is simply
                about making something timeless more accessible.
              </Typography>
              <Typography paragraph>Our vision is simple.</Typography>
              <Typography paragraph>
                The newly married couple in Canada should be able to participate
                in a puja at a temple in Varanasi... That man struggling with a
                terminal illness, with a little ray of hope, should still get a
                chance to offer his prayers. The Gen Z atheist, who never
                believed in God, should be able to light a diya in a temple when
                she finally feels the quiet need for divine grace.
              </Typography>
              <Typography paragraph>
                Temple Dekho is not about replacing the temple—it is just about
                bringing it a bit closer to you.
              </Typography>
            </Box>
          </Box>

          <Box mb={5} className="teamDetailsSec cardBg">
            <div className="team-section text-center mb-8">
              <div className="sectionHead text-center style-4 ">
                <small className="title_small d-none"></small>

                <Typography className="mainTitle mb-20">
                  The Minds <span>Behind</span>
                </Typography>
              </div>
              <Typography className="text-lg">
                Our team is a dynamic blend of innovators, technologists, and
                problem-solvers passionate about leveraging cutting-edge AI to
                drive impactful solutions. With diverse expertise and a shared
                vision, we are dedicated to creating secure, sustainable, and
                user-centric technologies that shape the future.
              </Typography>
            </div>
            <Box className="team sectionPadding pb40 style6">
              <Grid container spacing={3}>
                {/* Mr. Subham Sen */}
                <Grid item xs={12} sm={4} lg={3}>
                  <Box className="teamCard style6">
                    <Box className="imgCover">
                      <img src={SubhamSen} alt="Mr. Subham Sen" />
                      <Box className="socialIcons">
                        <Link
                          href="https://www.linkedin.com/in/subhamsen/"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <LinkedInIcon />
                        </Link>
                      </Box>
                    </Box>
                    <Box className="readBioButton">
                      <Button
                        onClick={() => handleReadBioOpen("subhamSenModal")}
                        variant="contained"
                      >
                        Read Bio
                      </Button>
                    </Box>
                    {/* Modal */}
                    <Dialog
                      open={openBioId === "subhamSenModal"}
                      onClose={handleReadBioClose}
                      className="dialogContainer"
                    >
                      <Stack
                        direction="row"
                        justifyContent="flex-end"
                        margin="8px 10px 0"
                      >
                        <IconButton
                          aria-label="close"
                          onClick={handleReadBioClose}
                          className="topCloseBtn"
                        >
                          <CloseIcon />
                        </IconButton>
                      </Stack>
                      <DialogContent className="innerContent">
                        <Box className="readBioDialog">
                          <Box className="leftImg">
                            <img
                              src={SubhamSen}
                              className="rounded"
                              width="150"
                              height="150"
                              alt="Profile Image"
                            />
                            <Link
                              href="https://www.linkedin.com/in/subhamsen/"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <LinkedInIcon />
                            </Link>
                          </Box>
                          <Box className="bioDescription">
                            <Typography className="titleName" variant="h4">
                              Mr. Subham Sen
                            </Typography>
                            <Typography className="textMuted">
                              Chief Technology Officer
                            </Typography>
                            <Typography className="someWords">
                              <strong>Subham Sen</strong> is the Co-founder and
                              CTO at Temple Dekho, where he leads the platform’s
                              overall technology vision and execution. With
                              decades of experience in enterprise tech, he has
                              been instrumental in building a secure, scalable,
                              and future-ready digital foundation that powers
                              the Temple Dekho experience.
                            </Typography>
                            <Typography className="someWords">
                              Subham is deeply passionate about leveraging
                              technology to solve meaningful problems. At Temple
                              Dekho, he focuses on integrating modern
                              innovations—from real-time systems to intuitive
                              user journeys—that help bring spirituality closer
                              to people, no matter where they are. His approach
                              blends technical depth with a strong sense of
                              purpose.
                            </Typography>
                            <Typography className="someWords">
                              A lifelong learner and hands-on problem solver,
                              Subham believes that tradition and innovation can
                              coexist beautifully—and Temple Dekho is proof of
                              that vision.
                            </Typography>
                            <Typography className="someWords">
                              Before co-founding Temple Dekho, Subham held over
                              two decades of leadership and enterprise
                              architecture roles at tech giants like Ericsson
                              and Samsung, where he led mission-critical,
                              high-impact technology projects.
                            </Typography>
                          </Box>
                        </Box>
                      </DialogContent>
                    </Dialog>
                    <Box className="info">
                      <Typography variant="h6">Mr. Subham Sen</Typography>
                      <Typography>
                        Co-founder & Chief Technology Officer
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                {/* Uday Shankar Prasad */}
                <Grid item xs={12} sm={4} lg={3}>
                  <Box className="teamCard style6">
                    <Box className="imgCover">
                      <img src={UdayShankar} alt="Uday Shankar Prasad" />
                      <Box className="socialIcons">
                        <Link
                          href="https://www.linkedin.com/in/uday-shankar-prasad/"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <LinkedInIcon />
                        </Link>
                      </Box>
                    </Box>
                    <Box className="readBioButton">
                      <Button
                        onClick={() =>
                          handleReadBioOpen("udayShankarPrasadModal")
                        }
                        variant="contained"
                      >
                        Read Bio
                      </Button>
                    </Box>
                    {/* Modal */}
                    <Dialog
                      open={openBioId === "udayShankarPrasadModal"}
                      onClose={handleReadBioClose}
                      className="dialogContainer"
                    >
                      <Stack
                        direction="row"
                        justifyContent="flex-end"
                        margin="8px 10px 0"
                      >
                        <IconButton
                          aria-label="close"
                          onClick={handleReadBioClose}
                          className="topCloseBtn"
                        >
                          <CloseIcon />
                        </IconButton>
                      </Stack>
                      <DialogContent className="innerContent">
                        <Box className="readBioDialog">
                          <Box className="leftImg">
                            <img
                              src={UdayShankar}
                              className="rounded"
                              width="150"
                              height="150"
                              alt="Profile Image"
                            />
                            <Link
                              href="https://www.linkedin.com/in/uday-shankar-prasad/"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <LinkedInIcon />
                            </Link>
                          </Box>
                          <Box className="bioDescription">
                            <Typography className="titleName" variant="h4">
                              Uday Shankar Prasad
                            </Typography>
                            <Typography className="textMuted">
                              Co-Founder & Chief Solutions Architect
                            </Typography>
                            <Typography className="someWords">
                              <strong>Uday Shankar Prasad</strong> is a seasoned
                              Chief Solution Architect and visionary
                              technologist with over two decades of global
                              experience in designing robust, enterprise-grade
                              solutions. At Temple Dekho, he plays a pivotal
                              role in shaping the company's long-term technology
                              strategy, ensuring the platform is scalable,
                              resilient, and aligned with the evolving needs of
                              users.
                            </Typography>
                            <Typography className="someWords">
                              With a strong foundation in enterprise systems,
                              Uday brings deep technical expertise to the
                              development of high-performing and future-ready
                              architectures. His ability to bridge business
                              objectives with technical execution has been
                              instrumental in delivering the seamless, reliable
                              user experience that defines Temple Dekho.
                            </Typography>
                            <Typography className="someWords">
                              Before joining Temple Dekho, Uday held senior
                              technology leadership roles at renowned
                              organizations such as Accenture and Ericsson,
                              where he led cutting-edge initiatives in Industry
                              4.0, smart manufacturing systems, and autonomous
                              drone technologies. Recognized for guiding
                              cross-functional teams through complex digital
                              transformations, he is deeply committed to
                              leveraging technology to provide meaningful
                              spiritual access in today’s digital world.
                            </Typography>
                          </Box>
                        </Box>
                      </DialogContent>
                    </Dialog>
                    <Box className="info">
                      <Typography variant="h6">Uday Shankar Prasad</Typography>
                      <Typography>
                        Co-Founder & Chief Solutions Architect
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                {/* Saheli Das */}
                <Grid item xs={12} sm={4} lg={3}>
                  <Box className="teamCard style6">
                    <Box className="imgCover">
                      <img src={SaheliDas} alt="Saheli Das" />
                      <Box className="socialIcons">
                        <Link
                          href="https://www.linkedin.com/in/saheli-das-5a006579/"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <LinkedInIcon />
                        </Link>
                      </Box>
                    </Box>
                    <Box className="readBioButton">
                      <Button
                        onClick={() => handleReadBioOpen("saheliDasModal")}
                        variant="contained"
                      >
                        Read Bio
                      </Button>
                    </Box>
                    {/* Modal */}
                    <Dialog
                      open={openBioId === "saheliDasModal"}
                      onClose={handleReadBioClose}
                      className="dialogContainer"
                    >
                      <Stack
                        direction="row"
                        justifyContent="flex-end"
                        margin="8px 10px 0"
                      >
                        <IconButton
                          aria-label="close"
                          onClick={handleReadBioClose}
                          className="topCloseBtn"
                        >
                          <CloseIcon />
                        </IconButton>
                      </Stack>
                      <DialogContent className="innerContent">
                        <Box className="readBioDialog">
                          <Box className="leftImg">
                            <img
                              src={SaheliDas}
                              className="rounded"
                              width="150"
                              height="150"
                              alt="Profile Image"
                            />
                            <Link
                              href="https://www.linkedin.com/in/saheli-das-5a006579/"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <LinkedInIcon />
                            </Link>
                          </Box>
                          <Box className="bioDescription">
                            <Typography className="titleName" variant="h4">
                              Saheli Das
                            </Typography>
                            <Typography className="textMuted">
                              Co-founder, Product Owner, Head of Cloud Platform
                              & Marketing
                            </Typography>
                            <Typography className="someWords">
                              <strong>Saheli Das</strong> is a strategic product
                              leader and technology thinker who leads product,
                              cloud platforms, marketing, and growth at Temple
                              Dekho. With over a decade of experience in
                              building and scaling cloud-based systems, she
                              plays a key role in shaping the platform’s digital
                              journey—from backend infrastructure to frontend
                              user experience.
                            </Typography>
                            <Typography className="someWords">
                              Saheli brings a rare combination of technical
                              insight and business acumen, ensuring Temple
                              Dekho’s platform is not only robust and secure,
                              but also intuitive and user-focused. Her expertise
                              lies in translating user needs into scalable
                              solutions while driving brand visibility and
                              engagement across channels.
                            </Typography>
                            <Typography className="someWords">
                              Prior to co-founding Temple Dekho, Saheli worked
                              with global firms like Ericsson and Deloitte,
                              where she helped design and deliver advanced
                              cloud-driven platforms across IoT and Industry 4.0
                              domains. She now leads Temple Dekho’s growth with
                              a clear focus: making faith more accessible
                              through technology and thoughtful storytelling.
                            </Typography>
                          </Box>
                        </Box>
                      </DialogContent>
                    </Dialog>
                    <Box className="info">
                      <Typography variant="h6">Saheli Das</Typography>
                      <Typography>
                        Co-founder, Product Owner, Head of Cloud Platform &
                        Marketing
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                {/* Avinash Pandey */}
                <Grid item xs={12} sm={4} lg={3}>
                  <Box className="teamCard style6">
                    <Box className="imgCover">
                      <img src={AvinashPandey} alt="Avinash Pandey" />
                      <Box className="socialIcons">
                        <Link
                          href="https://www.linkedin.com/in/avinash-pandey-5aa44a8a/"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <LinkedInIcon />
                        </Link>
                      </Box>
                    </Box>
                    <Box className="readBioButton">
                      <Button
                        onClick={() => handleReadBioOpen("avinashPandeyModal")}
                        variant="contained"
                      >
                        Read Bio
                      </Button>
                    </Box>
                    {/* Modal */}
                    <Dialog
                      open={openBioId === "avinashPandeyModal"}
                      onClose={handleReadBioClose}
                      className="dialogContainer"
                    >
                      <Stack
                        direction="row"
                        justifyContent="flex-end"
                        margin="8px 10px 0"
                      >
                        <IconButton
                          aria-label="close"
                          onClick={handleReadBioClose}
                          className="topCloseBtn"
                        >
                          <CloseIcon />
                        </IconButton>
                      </Stack>
                      <DialogContent className="innerContent">
                        <Box className="readBioDialog">
                          <Box className="leftImg">
                            <img
                              src={AvinashPandey}
                              className="rounded"
                              width="150"
                              height="150"
                              alt="Profile Image"
                            />
                            <Link
                              href="https://www.linkedin.com/in/avinash-pandey-5aa44a8a/"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <LinkedInIcon />
                            </Link>
                          </Box>
                          <Box className="bioDescription">
                            <Typography className="titleName" variant="h4">
                              Avinash Pandey
                            </Typography>
                            <Typography className="textMuted">
                              Co-founder & Head of Application Development
                            </Typography>
                            <Typography className="someWords">
                              <strong>Avinash Pandey</strong> is a core
                              technology leader at Temple Dekho, where he heads
                              application development and drives the platform’s
                              digital innovation from the ground up. Known for
                              his sharp coding skills and problem-solving
                              mindset, he plays a key role in building seamless,
                              secure, and high-performance experiences for
                              devotees across the Temple Dekho ecosystem.
                            </Typography>
                            <Typography className="someWords">
                              At Temple Dekho, Avinash leads a dynamic team
                              focused on developing user-first applications that
                              scale with demand and adapt to real-world
                              spiritual journeys. His deep understanding of
                              modern tech stacks ensures that every feature is
                              thoughtfully engineered to deliver impact, trust,
                              and ease of use.
                            </Typography>
                            <Typography className="someWords">
                              Before co-founding Temple Dekho, Avinash worked
                              with leading firms like Ericsson and Spectraforce,
                              contributing to advanced technology solutions in
                              IoT, Industry 4.0, and autonomous drones. Today,
                              he brings that same spirit of innovation to
                              faith-tech—shaping the backend of Temple Dekho to
                              feel as personal as the prayer itself.
                            </Typography>
                          </Box>
                        </Box>
                      </DialogContent>
                    </Dialog>
                    <Box className="info">
                      <Typography variant="h6">Avinash Pandey</Typography>
                      <Typography>
                        Co-founder & Head of Application Development
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                {/* Sarbajit Dutta Roy */}
                <Grid item xs={12} sm={4} lg={3}>
                  <Box className="teamCard style6">
                    <Box className="imgCover">
                      <img src={SarbajitDutta} alt="Sarbajit Dutta Roy" />
                      <Box className="socialIcons">
                        <Link
                          href="https://www.linkedin.com/in/sarbajit-dutta-roy-78759322/"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <LinkedInIcon />
                        </Link>
                      </Box>
                    </Box>
                    <Box className="readBioButton">
                      <Button
                        onClick={() =>
                          handleReadBioOpen("sarbajitDuttaRoyModal")
                        }
                        variant="contained"
                      >
                        Read Bio
                      </Button>
                    </Box>
                    {/* Modal */}
                    <Dialog
                      open={openBioId === "sarbajitDuttaRoyModal"}
                      onClose={handleReadBioClose}
                      className="dialogContainer"
                    >
                      <Stack
                        direction="row"
                        justifyContent="flex-end"
                        margin="8px 10px 0"
                      >
                        <IconButton
                          aria-label="close"
                          onClick={handleReadBioClose}
                          className="topCloseBtn"
                        >
                          <CloseIcon />
                        </IconButton>
                      </Stack>
                      <DialogContent className="innerContent">
                        <Box className="readBioDialog">
                          <Box className="leftImg">
                            <img
                              src={SarbajitDutta}
                              className="rounded"
                              width="150"
                              height="150"
                              alt="Profile Image"
                            />
                            <Link
                              href="https://www.linkedin.com/in/sarbajit-dutta-roy-78759322/"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <LinkedInIcon />
                            </Link>
                          </Box>
                          <Box className="bioDescription">
                            <Typography className="titleName" variant="h4">
                              Sarbajit Dutta Roy
                            </Typography>
                            <Typography className="textMuted">
                              Co-founder & Head of Experience Design & Quality
                              Assurance
                            </Typography>
                            <Typography className="someWords">
                              <strong>Sarbajit Dutta Roy</strong> is a creative
                              force and design strategist at Temple Dekho, where
                              he leads Experience Design and Quality Assurance
                              across the entire platform. With over a decade of
                              experience in UX design and product management, he
                              brings a refined sense of usability, visual
                              clarity, and emotional resonance to every user
                              interaction.
                            </Typography>
                            <Typography className="someWords">
                              Sarbajit is the mind behind the Temple Dekho's
                              visual identity and user journey—ensuring that
                              every design element, screen flow, and interface
                              feels intuitive, inclusive, and spiritually
                              aligned. From core dashboards to campaign
                              creatives, his design language defines the
                              platform’s distinct personality.
                            </Typography>
                            <Typography className="someWords">
                              He has previously worked with global firms like
                              PwC, Cognizant, and Ericsson where he crafted user
                              experiences for complex digital products. Today,
                              he brings that expertise to Temple Dekho—making
                              sure that every pixel serves a purpose, and every
                              interaction brings the user closer to their faith
                              in a thoughtful, beautiful, and seamless way.
                            </Typography>
                          </Box>
                        </Box>
                      </DialogContent>
                    </Dialog>
                    <Box className="info">
                      <Typography variant="h6">Sarbajit Dutta Roy</Typography>
                      <Typography>
                        Co-founder & Head of Experience Design & Quality
                        Assurance
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                {/* Debaditya Mandal */}
                <Grid item xs={12} sm={4} lg={3}>
                  <Box className="teamCard style6">
                    <Box className="imgCover">
                      <img src={Debaditya} alt="Debaditya Mandal" />
                      <Box className="socialIcons">
                        <Link
                          href="https://www.linkedin.com/in/debaditya-mandal/"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <LinkedInIcon />
                        </Link>
                      </Box>
                    </Box>
                    <Box className="readBioButton">
                      <Button
                        onClick={() =>
                          handleReadBioOpen("debadityaMandalModal")
                        }
                        variant="contained"
                      >
                        Read Bio
                      </Button>
                    </Box>
                    {/* Modal */}
                    <Dialog
                      open={openBioId === "debadityaMandalModal"}
                      onClose={handleReadBioClose}
                      className="dialogContainer"
                    >
                      <Stack
                        direction="row"
                        justifyContent="flex-end"
                        margin="8px 10px 0"
                      >
                        <IconButton
                          aria-label="close"
                          onClick={handleReadBioClose}
                          className="topCloseBtn"
                        >
                          <CloseIcon />
                        </IconButton>
                      </Stack>
                      <DialogContent className="innerContent">
                        <Box className="readBioDialog">
                          <Box className="leftImg">
                            <img
                              src={Debaditya}
                              className="rounded"
                              width="150"
                              height="150"
                              alt="Profile Image"
                            />
                            <Link
                              href="https://www.linkedin.com/in/debaditya-mandal/"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <LinkedInIcon />
                            </Link>
                          </Box>
                          <Box className="bioDescription">
                            <Typography className="titleName" variant="h4">
                              Debaditya Mandal
                            </Typography>
                            <Typography className="textMuted">
                              Co-founder & Head of AI Research
                            </Typography>
                            <Typography className="someWords">
                              <strong>Debaditya Mandal</strong> is a co-founder
                              of Temple Dekho and plays a key strategic role in
                              shaping its AI-led vision. His contributions focus
                              on enabling intelligent, conversational user
                              experiences that bring spiritual discovery closer
                              to people through technology.
                            </Typography>
                            <Typography className="someWords">
                              With deep expertise in Natural Language
                              Processing, Natural Language Understanding,
                              Computational Linguistics, and Machine Learning,
                              Debaditya guides the evolution of Temple Dekho’s
                              AI capabilities—particularly Valmiki, the
                              platform’s conversational layer—with a focus on
                              cultural sensitivity and contextual understanding.
                            </Typography>
                            <Typography className="someWords">
                              Prior to Temple Dekho, he was associated with
                              Saarthi and Armsoftech Private Limited, where he
                              worked on building advanced conversational
                              systems. His passion for combining language,
                              culture, and technology aligns closely with Temple
                              Dekho’s mission of making devotion more
                              accessible, intuitive, and personalized in the
                              digital age.
                            </Typography>
                          </Box>
                        </Box>
                      </DialogContent>
                    </Dialog>
                    <Box className="info">
                      <Typography variant="h6">Debaditya Mandal</Typography>
                      <Typography>Co-founder & Head of AI Research</Typography>
                    </Box>
                  </Box>
                </Grid>

                {/* Sourav Saha */}
                <Grid item xs={12} sm={4} lg={3}>
                  <Box className="teamCard style6">
                    <Box className="imgCover">
                      <img src={SouravSaha} alt="Sourav Saha" />
                      <Box className="socialIcons">
                        <Link
                          href="https://www.linkedin.com/in/me-souravsaha/"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <LinkedInIcon />
                        </Link>
                      </Box>
                    </Box>
                    <Box className="readBioButton">
                      <Button
                        onClick={() => handleReadBioOpen("souravSahaModal")}
                        variant="contained"
                      >
                        Read Bio
                      </Button>
                    </Box>
                    {/* Modal */}
                    <Dialog
                      open={openBioId === "souravSahaModal"}
                      onClose={handleReadBioClose}
                      className="dialogContainer"
                    >
                      <Stack
                        direction="row"
                        justifyContent="flex-end"
                        margin="8px 10px 0"
                      >
                        <IconButton
                          aria-label="close"
                          onClick={handleReadBioClose}
                          className="topCloseBtn"
                        >
                          <CloseIcon />
                        </IconButton>
                      </Stack>
                      <DialogContent className="innerContent">
                        <Box className="readBioDialog">
                          <Box className="leftImg">
                            <img
                              src={SouravSaha}
                              className="rounded"
                              width="150"
                              height="150"
                              alt="Profile Image"
                            />
                            <Link
                              href="https://www.linkedin.com/in/me-souravsaha/"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <LinkedInIcon />
                            </Link>
                          </Box>
                          <Box className="bioDescription">
                            <Typography className="titleName" variant="h4">
                              Sourav Saha
                            </Typography>
                            <Typography className="textMuted">
                              Co-founder & Head of Engineering (Backend)
                            </Typography>
                            <Typography className="someWords">
                              <strong>Sourav Saha</strong> is a co-founder of
                              Temple Dekho and Backend Systems Advisor, with a
                              strong track record in building scalable,
                              high-performance digital infrastructures. He
                              brings deep expertise in system design and backend
                              architecture, focused on creating clean, reliable,
                              and future-ready platforms.
                            </Typography>
                            <Typography className="someWords">
                              At Temple Dekho, Sourav leads the design and
                              advisory of core backend systems that support the
                              platform’s seamless functionality. He plays a
                              critical role in building the technical backbone
                              behind intelligent features—particularly
                              contributing to the infrastructure powering
                              Valmiki, Temple Dekho’s AI-driven conversational
                              layer. His approach ensures that the platform
                              remains fast, secure, and capable of serving a
                              growing devotee base with reliability.
                            </Typography>
                            <Typography className="someWords">
                              With over a decade of experience in product
                              development, Sourav has held engineering roles at
                              leading tech firms such as Ericsson, Samsung R&D,
                              and Motorola Mobility. His expertise across
                              diverse tech stacks continues to guide Temple
                              Dekho’s technology roadmap with both precision and
                              purpose.
                            </Typography>
                          </Box>
                        </Box>
                      </DialogContent>
                    </Dialog>
                    <Box className="info">
                      <Typography variant="h6">Sourav Saha</Typography>
                      <Typography>
                        Co-founder & Head of Engineering (Backend)
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>

          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
            mb={5}
            className="atGlanceSec thePeople cardBg"
          >
            <Box className="glanceText">
              <Typography variant="h5" gutterBottom mb={2} className="fw-bold">
                The People
              </Typography>

              <Typography paragraph>
                At the heart of Temple Dekho is a dynamic blend of professionals
                with expertise across technology, operations, product,
                marketing, and customer experience. This passionate and
                cross-functional squad brings a strong sense of ownership and a
                shared commitment to excellence, enabling us to deliver reliable
                and scalable spiritual solutions for modern users.
              </Typography>
              <Typography paragraph>
                The founding team at the forefront shapes the roadmap ahead—not
                just as leaders, but as mentors who guide, support, and
                challenge the team to evolve constantly. United by purpose and
                driven by innovation, every member brings a unique perspective
                to the table—fuelling our mission with creativity, agility, and
                zeal.
              </Typography>
              <Typography paragraph>
                Temple Dekho is more than a platform—together we are crafting a
                movement that is inclusive, transformative, and deeply rooted in
                values.
              </Typography>
            </Box>
            <Box className="peopleImg">
              <img src={peopleImg} alt="Image"></img>
            </Box>
          </Stack>

          <Box mb={5} className="fuellingSec cardBg">
            <Typography variant="h5" mb={2} gutterBottom className="fw-bold">
              Fuelling Our Mission
            </Typography>
            <Typography paragraph>
              Temple Dekho is strategically backed by Sona Biscuits Pvt. Ltd.,
              one of the leading FMCG brands in Eastern India. With decades of
              business excellence and a legacy of trust, Sona Biscuits brings
              more than just strategic support. Their belief in our vision
              reinforces our commitment to building a scalable and spiritually
              enriching platform that empowers devotees worldwide to stay
              connected with their faith.
            </Typography>
          </Box>
          <Box mb={5} className="btsTd cardBg">
            <Typography variant="h5" mb={2} gutterBottom className="fw-bold">
              BTS @ Temple Dekho
            </Typography>
            <Typography paragraph>
              What’s it like working at the intersection of devotion and
              digital?
            </Typography>
            <Typography paragraph>
              Think of temple bells in the background of Teams Meets!
            </Typography>
            <Typography paragraph>
              Life at Temple Dekho is fast paced with a mix of high-energy
              brainstorms, chai-fueled coding sprints, product huddles and puja
              planning. Every day here brings something unexpected—and we
              wouldn’t have it any other way. We are a bunch of serious
              people—but not too serious all the time.
            </Typography>
            <Typography paragraph>
              Whether we’re building product features or planning our next
              temple visit (field research, obviously), the vibe is
              collaborative, curious, and occasionally chaotic—in the best way.
              Mondays start with mantras, Fridays end with food, and somewhere
              in between, we get a lot done.
            </Typography>
            <Typography paragraph>
              At Temple Dekho, the ideas are fearless, and the team is full of
              faith (and foodies).
            </Typography>
          </Box>
          <Box mb={5} className="certification cardBg">
            <Typography variant="h5" mb={2} gutterBottom className="fw-bold">
              Achievements
            </Typography>
            <Typography paragraph>
              Temple Dekho is ISO 27001 certified, SOC 2 Type II compliant, and
              PCI DSS Level 1 validated for secure data handling.
            </Typography>
            <Box className="iconList">
              <img src={isoIcon} alt="ISO" className="isoImg" />
              <img src={socIcon} alt="SOC" className="socImg" />
              <img src={dssIcon} alt="DSS" className="dssImg" />
            </Box>
          </Box>
          <Box mb={5}>
            <Box className="blessYSiso">
              <Box className="blessIsoImg">
                <LazyLoadImage
                  effect="blur"
                  wrapperProps={{
                    // If you need to, you can tweak the effect transition using the wrapper style.
                    style: { transitionDelay: "1s" },
                  }}
                  src={onlyBlessYSiso}
                  alt="only BlessYSiso"
                />
              </Box>
            </Box>
            <Box className="socialMediaSec">
              <TitleBlockCard title={t(LOCALIZATION.Join_Into_Our_Community)} />
              <SocialMediaAdd />
            </Box>
          </Box>
        </div>
      </Container>
    </Box>
  );
};

export default AboutView;
